import React, { createContext, useEffect, useRef, useState } from 'react';
import {
	postV1AuthChangePassword,
	postV1AuthForgotPassword,
	postV1AuthChangeUserPassword,
	// getV1UserId,
	postV1AuthLogin,
	postV1AuthResetPassword,
	setBaseUrl,
	postV1AuthChangeUserPasswordByAdmin
} from '../api-client/api.rbac/api';
import { auth } from '../constants/auth';
import { useLocation, matchRoutes, useNavigate } from 'react-router-dom';
import { Pathname } from '../constants/pathnames/pathname'; // Removed - should be passed as props or configured by consuming app
import { getToken } from '../utils/common';
import { enqueueSnackbar } from 'notistack';
import { Socket, io } from 'socket.io-client';
import { generateRouteWithId } from '../utils/route-utils';
import { getApiConfig } from '../utils/api-config';
import {
	PathnameAccounting,
	PathnameGenerator
} from '../constants/pathnames/pathname.accounting';

import {
	PathnameInventory,
	PathnameGenerator as PathnameGeneratorInventory
} from '../constants/pathnames/pathname.inventory';

import {
	PathnameManufacturing,
	PathnameGenerator as PathnameGeneratorManufacuring
} from '../constants/pathnames/pathname.manufacturing';

import {
	PathnamePurchase,
	PathnameGenerator as PathnameGeneratorPurchase
} from '../constants/pathnames/pathname.procurement';

import {
	PathnameCrm,
	PathnameGenerator as PathnameGeneratorCrm
} from '../constants/pathnames/pathname.crm';

import {
	PathnameRental,
	PathnameGenerator as PathnameGeneratorRental
} from '../constants/pathnames/pathname.rental';

import { ROUTES as RENTALROUTES } from '../constants/pathnames/pathname.rental';

import CustomSnackbar from '../components/custom-snackbar/custom-snackbar.tsx';
import { RbacApi } from '@/api-client/index.ts';
import { useContext } from 'react';

export interface LoginFormValues {
	email: string;
	password: string;
}

export interface resetFormValues {
	email: string;
}

export interface resetPasswordValues {
	token: string;
}

export interface changePasswordValues {
	password: string;
}

export interface changeUserPasswordValues {
	password: string;
}

interface ERPApiUser {
	id: string | number;
	first_name: string;
	last_name: string;
	middle_name: string;
	country_code: string;
	email: string;
	created_by: any;
	updated_by: any;
	created_at: string;
	updated_at: string;
	role_id: string;
	department_id: number;
	is_deleted: number;
	deleted_at: string | null;
	deleted_by: any;
	token: string;
	profile_image: string;
	company_id: number | string;
	currency_data?: {
		id: number;
		symbol: string;
		currency_name: string;
		number_format: string;
		position: string | null;
		fraction_unit: string | null;
		accounting_fraction_unit?: string | null;
	};
	accessCompanies?: any[]
}
interface ERPUser extends Omit<ERPApiUser, 'token'> {
	full_name: string;
}

// Export types for external use
export type User = ERPUser;
export type AuthContextType = DefaultProviderProps;

export interface DefaultProviderProps {
	user: ERPUser | null;
	loading: boolean;
	authToken: string;
	setUser: React.Dispatch<React.SetStateAction<ERPUser | null>>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	login: (
		params: LoginFormValues,
		callback: (err: string) => void
	) => Promise<void>;
	logout: () => Promise<void>;
	forgotPassword: (
		params: resetFormValues,
		callback: (err: string) => void
	) => Promise<void>;
	resetPassword: (
		params: resetPasswordValues,
		callback: (err: string) => void
	) => Promise<void>;
	changePassword: (
		params: changePasswordValues,
		callback: (err: string) => void
	) => Promise<void>;
	changeUserPassword: (
		params: changeUserPasswordValues,
		callback: (err: string) => void
	) => Promise<void>;
}

const defaultProvider: DefaultProviderProps = {
	user: null,
	loading: true,
	authToken: '',
	setUser: () => null,
	setLoading: () => Boolean,
	login: () => Promise.resolve(),
	logout: () => Promise.resolve(),
	forgotPassword: () => Promise.resolve(),
	resetPassword: () => Promise.resolve(),
	changePassword: () => Promise.resolve(),
	changeUserPassword: () => Promise.resolve()
};

const AuthContext = createContext<DefaultProviderProps>(defaultProvider);

interface AuthProviderProps {
	children: React.ReactNode;
	publicRoutes?: string[]; // Optional prop to allow customization of public routes
}

// Base URL will be set at runtime via ERPUIProvider

const AuthProvider: React.FC<AuthProviderProps> = ({ children, publicRoutes = [] }) => {
	// ** States
	const [user, setUser] = useState(defaultProvider.user);
	const [authToken, setAuthToken] = useState<string>('');
	const [loading, setLoading] = useState(defaultProvider.loading);
	const socketRef = useRef<Socket | null>(null);

	// ** Hooks
	const navigate = useNavigate();
	const location = useLocation();
	const isJsonParse = (string?: string | null) => {
		if (!string) return false;

		try {
			return JSON.parse(string);
		} catch (error) {
			return false;
		}
	};

	const getNotificationUrl = (data: any) => {
		const { moduleName = '', moduleId = '', notificationData = {} } = data;
		const key = moduleName.toLowerCase();

		const routeMap: Record<string, () => string> = {
			'sales invoice': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_SALES_INVOICES), moduleId.toString()),

			'asset transfers': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_ASSET_TRANSFER), moduleId.toString()),

			'budgets': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_BUDGET), moduleId.toString()),

			'cash expense': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_CASH_EXPENSE), moduleId.toString()),

			'credit notes': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_CREDIT_NOTE), moduleId.toString()),

			'debit notes': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_DEBIT_NOTE), moduleId.toString()),

			'expense reimbursement': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_EXPENSE_REIMBURSEMENT), moduleId.toString()),

			'journal entries': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_JOURNAL_ENTRY), moduleId.toString()),

			'payment entries': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_PAYMENT_ENTRY), moduleId.toString()),

			'purchase invoice': () =>
				generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_PURCHASE_INVOICE), moduleId.toString()),

			'stock transfer': () =>
				generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_STOCK_TRANSFER), moduleId.toString()),

			'bills of materials': () =>
				generateRouteWithId(PathnameGeneratorManufacuring(PathnameManufacturing.VIEW_BILL_OF_MATERIAL), moduleId.toString()),

			'purchase agreement': () =>
				generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_AGREEMENT), moduleId.toString()),

			'purchase orders': () =>
				generateRouteWithId(notificationData?.data?.is_rental == 1 ? RENTALROUTES.VIEW : PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_ORDER), moduleId.toString()),

			'purchase request': () =>
				generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_ORDER), moduleId.toString()),

			'vra': () =>
				generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_VENDOR_RETURNS), moduleId.toString()),

			'sl customer returns': () =>
				generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_CUSTOMER_RETURNS), moduleId.toString()),

			'sales order': () =>
				generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_SALES_ORDER), moduleId.toString()),

			'rental order': () =>
				generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_RENTAL_ORDER), moduleId.toString()),

			'replacement order': () =>
				generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_RENTAL_ORDER), moduleId.toString()),

			'rental agreement': () =>
				generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_AGREEMENT), moduleId.toString()),

			'cross hire order': () =>
				generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_CROSS_HIRE_ORDER), moduleId.toString()),

			'cross hire request': () =>
				generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_CROSS_HIRE_REQUEST), moduleId.toString()),
		};

		return routeMap[key]?.() || '/dashboard';
	};

	useEffect(() => {
		setLoading(true);
		// initAuth()
		const storedToken = globalThis.localStorage.getItem(auth.storageTokenKeyName);
		const storedUser =
			isJsonParse(globalThis.localStorage.getItem(auth.storageUserKeyName)) ?? null;

		const allPublicRoutes = [Pathname.LOGIN, Pathname.FORGOT_PASSWORD, Pathname.RESET_PASSWORD, Pathname.CHANGE_PASSWORD, ...publicRoutes];

		if (!storedToken && !storedUser) {
			if (Boolean(!matchRoutes(allPublicRoutes.map((route) => ({ path: route })), location))) {
				navigate(Pathname.LOGIN);
			}
			setLoading(false);
		} else {
			setUser(storedUser);
			setAuthToken(storedToken || '');
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!authToken) {
			if (socketRef.current) {
				socketRef.current?.disconnect();
				socketRef.current = null
			}
			console.warn("No auth token found, not connecting to socket.");
			return;
		}

		const config = getApiConfig();
		const socket = io(config.socketBaseUrl, {
			transports: ["websocket"],
			auth: { token: authToken },
			path: "/socket.io/",

		});
		socketRef.current = socket;

		socket.on("connect", () => {
			console.log("Socket connected:", socket.id);
			socket.onAny((event, ...args) => {
				console.log(`Received event: ${event}`, args);
			});
		});

		// socket event handler
		socket.on('notification', (data) => {
			const { message, notificationData } = data || {};
			let notificationContent: React.ReactNode = message;
			const url = getNotificationUrl(data);

			if (notificationData) {
				const { notification_type } = notificationData;

				switch (notification_type) {
					case 'addApprovers': {

						notificationContent = message;
						break;
					}

					case 'change_approval_status': {
						notificationContent = message;
						break;
					}

					default: {
						notificationContent = message;
						break;
					}
				}
			}

			enqueueSnackbar(String(message), {
				variant: 'default',
				anchorOrigin: { vertical: 'top', horizontal: 'center' },
				autoHideDuration: 5000,
				content: (key) => (
					<CustomSnackbar snackbarKey={key} message={notificationContent} data={notificationData} variant="default" linkUrl={url} />
				),
			});
		});


		socket.on("sales_invoice_viewed", (data) => {
			console.log("Invoice viewed:", data.invoiceId, "by user:", data.userId);
		});

		socket.on("error", (errMsg) => {
			enqueueSnackbar(`Socket error: ${errMsg}`, { variant: "error" });
			console.error("Socket error:", errMsg);
		});

		// Clean up on unmount
		return () => {
			if (socketRef.current) {
				socketRef.current.disconnect();
				socketRef.current = null
			}
		};
	}, [authToken]);

	const fetchRolesByUserId = async (userId: number | string, token: string) => {
		const response = await RbacApi.getV1RoleUserId({ id: userId, ...getToken(token) } as any);
		const perm = response?.data?.role || [];
		return JSON.stringify(perm);
	}

	const handleLogin = async (
		params: LoginFormValues,
		callback: (err: string) => void,
		shoudRedirect: boolean = true,
		redirectPath: string = Pathname.DASHBOARD
	) => {
		setLoading(true);
		const storedToken = globalThis.localStorage.getItem(auth.storageTokenKeyName);
		if (storedToken && user) {
			return navigate(Pathname.DASHBOARD);
		}

		try {
			const loginUser = await postV1AuthLogin({ ...params, ...getToken() });
			if (loginUser.success) {
				const data: ERPApiUser | null = loginUser?.data?.login || null;
				if (data) {
					const userData: ERPUser = {
						...data,
						full_name: `${data.first_name} ${data.last_name}`
					};
					const rolePermission = await fetchRolesByUserId(userData.id, data.token);
					
					globalThis.localStorage.setItem(auth.storageTokenKeyName, data.token);
					globalThis.localStorage.setItem(auth.storageUserKeyName, JSON.stringify(userData));
					globalThis.localStorage.setItem(auth.storageRolePermissionKeyName, rolePermission);
					globalThis.dispatchEvent(new Event("role-permission-changed"));

					setUser(userData);
					setAuthToken(data.token);
					setTimeout(() => {
						shoudRedirect && navigate(redirectPath);
						setLoading(false);
						delete data.token;
					}, 1000);
				}
			}
		} catch (error) {
			let message: string = 'Something went wrong';
			if (error instanceof Error) {
				message = isJsonParse(error.message).message || error.message;
			}
			setLoading(false);
			callback(message);
		}
	};

	const handleLogout = async () => {
		globalThis.localStorage.removeItem(auth.storageTokenKeyName);
		globalThis.localStorage.removeItem(auth.storageUserKeyName);
		globalThis.localStorage.removeItem(auth.storageRolePermissionKeyName);
		globalThis.dispatchEvent(new Event("role-permission-changed"));
		setUser(null);
		setAuthToken('');
		navigate(Pathname.LOGIN);
	};

	const handleForgotPassword = async (params: resetFormValues,
		callback: (type: boolean, err: string) => void) => {
		try {
			await postV1AuthForgotPassword({ ...params, ...getToken() });
			callback(true, "Reset Link sent to your email");
		} catch (error) {
			let message: string = 'Something went wrong';
			if (error instanceof Error) {
				message = isJsonParse(error.message).message || error.message;
			}

			callback(false, message);
		}
	};

	const handleResetPassword = async (params: resetPasswordValues,
		callback: (err: string) => void) => {
		try {
			const response = await postV1AuthResetPassword({ ...params, ...getToken() });
			callback(response);
		} catch (error) {
			let message: string = 'Something went wrong';
			if (error instanceof Error) {
				message = isJsonParse(error.message).message || error.message;
				callback(message);
			}
		}
	}

	const handleChangePassword = async (params: resetPasswordValues,
		callback: (err: string) => void) => {
		try {
			await postV1AuthChangePassword({ ...params, ...getToken() });
			callback(null);
		} catch (error) {
			let message: string = 'Something went wrong';
			if (error instanceof Error) {
				message = isJsonParse(error.message).message || error.message;
				callback(message);
			}
		}
		callback("reset");
	}

	const handleChangeUserPassword = async (params: resetPasswordValues,
		callback: (err: string, resType: boolean) => void) => {
		try {
			await postV1AuthChangeUserPassword({ ...params, ...getToken() });
			callback('Password Changed Successfully', true);
		} catch (error) {
			let message: string = 'Something went wrong';
			if (error instanceof Error) {
				message = isJsonParse(error.message).message || error.message;
				callback(message, false);
			}
		}
	}

	const handleChangeUserPasswordByAdmin = async (params: changePasswordValues,
		callback: (err: string, resType: boolean) => void) => {
		try {
			await postV1AuthChangeUserPasswordByAdmin({ ...params, ...getToken() });
			callback('Password Changed Successfully', true);
		} catch (error) {
			let message: string = 'Something went wrong';
			if (error instanceof Error) {
				message = isJsonParse(error.message).message || error.message;
				callback(message, false);
			}
		}
	}


	const values = {
		user,
		authToken,
		loading,
		setUser,
		setLoading,
		login: handleLogin,
		logout: handleLogout,
		forgotPassword: handleForgotPassword,
		resetPassword: handleResetPassword,
		changePassword: handleChangePassword,
		changeUserPassword: handleChangeUserPassword,
		changeUserPasswordByAdmin: handleChangeUserPasswordByAdmin
	};

	return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

// Custom hook to use the AuthContext
export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

export { AuthContext, AuthProvider };
